<template>
  <div class="sticky-scrollbar"
       ref="scrollbarEl"
       v-show="visible"
       @scroll="onScroll">
    <div class="sticky-scrollbar-inner" :style="{ width: scrollWidth + 'px' }"></div>
  </div>
</template>

<script setup>
import {ref, watch, onBeforeUnmount} from "vue"

const props = defineProps({
  target: {
    default: null
  },
  contentEl: {
    default: null
  }
})

const scrollbarEl = ref(null)
const scrollWidth = ref(0)
const visible = ref(false)
let syncing = false
let resizeObserver = null
let rafId = null

const update = () => {
  if(!props.target) return
  scrollWidth.value = props.target.scrollWidth
  visible.value = props.target.scrollWidth > props.target.clientWidth + 1
}

const requestSync = (source, targetEl) => {
  if(rafId) cancelAnimationFrame(rafId)
  rafId = requestAnimationFrame(() => {
    rafId = null
    syncing = true
    targetEl.scrollLeft = source.scrollLeft
    syncing = false
  })
}

const onTargetScroll = () => {
  if(syncing || !scrollbarEl.value) return
  requestSync(props.target, scrollbarEl.value)
}

const onScroll = () => {
  if(syncing || !props.target) return
  requestSync(scrollbarEl.value, props.target)
}

const attach = (el) => {
  el.addEventListener('scroll', onTargetScroll)
  resizeObserver = new ResizeObserver(() => update())
  resizeObserver.observe(el)
  if(props.contentEl) resizeObserver.observe(props.contentEl)
  update()
}

const detach = (el) => {
  if(el) el.removeEventListener('scroll', onTargetScroll)
  if(resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  if(rafId) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
}

watch(() => props.target, (el, oldEl) => {
  detach(oldEl)
  if(el) attach(el)
}, { immediate: true })

onBeforeUnmount(() => {
  detach(props.target)
})
</script>

<style scoped lang="scss">
.sticky-scrollbar {
  position: sticky;
  bottom: 0;
  overflow-x: auto;
  overflow-y: hidden;
  height: 14px;
  z-index: 5;
}
.sticky-scrollbar-inner {
  height: 1px;
}
</style>
